import React, { useState, useEffect } from 'react';
import { Helmet } from "react-helmet";

const ticketOptions90 = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
const ticketOptions75 = [8, 16, 24, 32, 40];

function TambolaTicketGenerator() {
  const [numTickets, setNumTickets] = useState(10);
  const [ticketType, setTicketType] = useState('90'); // '90' or '75'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const ticketOptions = ticketType === '75' ? ticketOptions75 : ticketOptions90;

  useEffect(() => {
    if (ticketType === '75' && !ticketOptions75.includes(numTickets)) {
      setNumTickets(ticketOptions75[0]);
    }
    if (ticketType === '90' && !ticketOptions90.includes(numTickets)) {
      setNumTickets(ticketOptions90[0]);
    }
  }, [ticketType, numTickets]);

  const handleDownload = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('https://match-scheduler-backend.onrender.com/api/bingo-tickets', {
      //   const response = await fetch('http://localhost:5001/api/bingo-tickets', {  
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ numTickets, ticketType })
      });
      if (!response.ok) throw new Error('Failed to generate tickets');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bingo-tickets-${numTickets}-${ticketType}ball.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Could not generate tickets. Please try again.');
    }
    setLoading(false);
  };

  const paypalDonateUrl = "https://www.paypal.com/donate/?hosted_button_id=FGPNV7YTRPPVE";

  const handleDonate = () => {
    if (window.gtag) {
      window.gtag('event', 'donate_click', {
        event_category: 'engagement',
        event_label: 'Donate Button'
      });
    }
    window.open(paypalDonateUrl, '_blank');
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f3f6fa 0%, #e3f0ff 100%)', padding: 0 }}>
      <div style={{ ...styles.container, boxShadow: '0 8px 32px rgba(0,0,0,0.10)', borderRadius: 24, marginTop: 48, marginBottom: 48, background: '#fff' }}>
        <Helmet>
          <title>Bingo Ticket Generator | Free Printable Housie/Bingo Tickets</title>
          <meta name="description" content="Generate professional, printable Tambola (Housie/Bingo) tickets for your next party or event. Free, cut-friendly PDF download, rules-compliant, and easy to use!" />
          <link rel="canonical" href="https://truth-ai-tech.com/bingo-tickets" />
        </Helmet>
        <h1 style={{ ...styles.title, fontSize: 34, marginBottom: 8 }}>Housie / Bingo Ticket Generator <span role="img" aria-label="bingo">🎱</span></h1>
        <p style={{ ...styles.intro, fontSize: 18, color: '#007bff', marginBottom: 24 }}>Generate beautiful, printable Tambola (Bingo) tickets for your next party or event. Download as PDF, ready to print and play!</p>
        <div style={{ borderTop: '1.5px solid #e3f0ff', margin: '24px 0 32px 0' }} />
        <div style={{ ...styles.formRow, flexWrap: 'wrap', justifyContent: 'center', gap: 32 }}>
          {/* Ticket Type Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f3f6fa', borderRadius: 999, padding: '8px 18px', boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}>
            <span style={{ fontWeight: 600, fontSize: 16, marginRight: 8 }}>Ticket Type:</span>
            <button
              type="button"
              onClick={() => setTicketType('90')}
              disabled={loading}
              style={{
                background: ticketType === '90' ? 'linear-gradient(90deg, #007bff 0%, #00c6ff 100%)' : '#fff',
                color: ticketType === '90' ? '#fff' : '#007bff',
                border: 'none',
                borderRadius: 999,
                padding: '8px 18px',
                fontWeight: 700,
                fontSize: 16,
                marginRight: 6,
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: ticketType === '90' ? '0 2px 8px rgba(0,123,255,0.10)' : 'none',
                display: 'flex', alignItems: 'center', gap: 6
              }}
            >
              <span role="img" aria-label="India">🇮🇳</span> 90-ball
            </button>
            <button
              type="button"
              onClick={() => setTicketType('75')}
              disabled={loading}
              style={{
                background: ticketType === '75' ? 'linear-gradient(90deg, #ff9800 0%, #ffd600 100%)' : '#fff',
                color: ticketType === '75' ? '#fff' : '#ff9800',
                border: 'none',
                borderRadius: 999,
                padding: '8px 18px',
                fontWeight: 700,
                fontSize: 16,
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: ticketType === '75' ? '0 2px 8px rgba(255,152,0,0.10)' : 'none',
                display: 'flex', alignItems: 'center', gap: 6
              }}
            >
              <span role="img" aria-label="US/UK">🇺🇸</span> 75-ball
            </button>
          </div>
          {/* Number of Tickets */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontWeight: 600, fontSize: 16, marginRight: 8 }}><span role="img" aria-label="ticket">🎟️</span> Number of Tickets:</span>
            <select
              value={numTickets}
              onChange={e => setNumTickets(Number(e.target.value))}
              style={{ ...styles.select, fontSize: 18, padding: '10px 18px', borderRadius: 12, minWidth: 90 }}
              disabled={loading}
            >
              {ticketOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          {/* Generate Button */}
          <button
            onClick={handleDownload}
            style={{
              ...styles.button,
              fontSize: 20,
              fontWeight: 800,
              padding: '16px 36px',
              borderRadius: 14,
              background: 'linear-gradient(90deg, #007bff 0%, #00c6ff 100%)',
              boxShadow: '0 2px 12px rgba(0,123,255,0.10)',
              display: 'flex', alignItems: 'center', gap: 10
            }}
            disabled={loading}
          >
            <span role="img" aria-label="bingo">🎱</span> {loading ? 'Generating...' : 'Generate & Download PDF'}
          </button>
        </div>
        {loading && <div style={{ ...styles.loading, fontSize: 18, marginTop: 18 }}><span className="loader" style={{ marginRight: 8 }} />Generating your tickets...</div>}
        {error && <div style={{ ...styles.error, background: '#ffeaea', color: '#d32f2f', borderRadius: 8, padding: 12, marginTop: 16 }}>{error}</div>}
        <div style={{ ...styles.info, background: '#e3f0ff', borderRadius: 12, marginTop: 32, fontSize: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            <li style={{ marginBottom: 6 }}>
              <span role="img" aria-label="page">📄</span> <b>{ticketType === '75' ? '4 tickets per page' : '10 tickets per page'}</b> — easy to cut, share, and distribute.
            </li>
            <li style={{ marginBottom: 6 }}><span role="img" aria-label="grid">🔢</span> <b>90-ball (Indian Tambola):</b> Classic 3 rows × 9 columns, numbers 1–90, every ticket unique.</li>
            <li style={{ marginBottom: 6 }}><span role="img" aria-label="grid">🔢</span> <b>75-ball (US/UK Bingo):</b> 5×5 grid, numbers 1–75, center is FREE, every ticket unique.</li>
            <li><span role="img" aria-label="printer">🖨️</span> <b>Print-friendly PDF</b> — compact, professional, and ready for your next game!</li>
          </ul>
        </div>
      </div> {/* End of main card/container */}
       {/* Donation Section */}
       <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl shadow-lg p-6 mb-8"
            style={{ maxWidth: 600, marginLeft: 'auto', marginRight: 'auto' }}>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img 
                  src="/logo.png" 
                  alt="Truth Logo" 
                  className="h-12 w-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <span 
                  className="text-2xl font-bold text-white hidden"
                  style={{ display: 'none' }}
                >
                  Truth
                </span>
              </div>
              <div className="flex flex-col items-center text-center">
                <h3 className="text-xl font-bold text-white mb-2">☕ Support Truth</h3>
                <p className="text-yellow-100 text-sm">If this tool helps you, consider buying me a coffee!</p>
              </div>
              <button
                onClick={handleDonate}
                className="bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition shadow-md flex items-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.067 8.478c.492.315.844.825.844 1.522 0 .697-.352 1.207-.844 1.522-.492.315-1.156.478-1.844.478H5.777c-.688 0-1.352-.163-1.844-.478C3.441 11.207 3.089 10.697 3.089 10c0-.697.352-1.207.844-1.522.492-.315 1.156-.478 1.844-.478h12.446c.688 0 1.352.163 1.844.478z"/>
                </svg>
                Donate with PayPal
              </button>
            </div>
          </div>
          </div>    
  );
}

const styles = {
  container: {
    maxWidth: 600,
    margin: '40px auto',
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    padding: 32,
    textAlign: 'center',
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: '#007bff',
    marginBottom: 24,
  },
  intro: {
    fontSize: 17,
    color: '#007bff',
    marginBottom: 18,
    fontWeight: 500,
  },
  formRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
    marginBottom: 24,
  },
  label: {
    fontSize: 18,
    fontWeight: 500,
    color: '#333',
  },
  select: {
    marginLeft: 10,
    fontSize: 16,
    padding: '6px 12px',
    borderRadius: 4,
    border: '1px solid #ccc',
  },
  button: {
    padding: '10px 20px',
    fontSize: 16,
    borderRadius: 4,
    border: 'none',
    backgroundColor: '#007bff',
    color: 'white',
    cursor: 'pointer',
    fontWeight: 600,
    boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
    transition: 'background 0.2s',
  },
  loading: {
    color: '#007bff',
    fontWeight: 600,
    marginTop: 16,
  },
  error: {
    color: 'red',
    fontWeight: 600,
    marginTop: 16,
  },
  info: {
    marginTop: 32,
    color: '#555',
    fontSize: 15,
    textAlign: 'left',
    background: '#f3f6fa',
    borderRadius: 8,
    padding: 16,
  },
  donateSection: {
    marginTop: 40,
    marginBottom: 0,
    background: 'linear-gradient(90deg, #FFD600 0%, #FF9800 100%)',
    borderRadius: 16,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    padding: 24,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  donateFlexRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
    width: '100%',
    maxWidth: 500,
  },
  logoWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  logoImg: {
    height: 48,
    width: 'auto',
    display: 'block',
  },
  logoText: {
    fontSize: 24,
    fontWeight: 700,
    color: '#fff',
  },
  donateTextWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    flex: 1,
  },
  donateTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: '#fff',
    marginBottom: 6,
  },
  donateDesc: {
    color: '#fffde7',
    fontSize: 14,
    marginBottom: 0,
  },
  donateButton: {
    background: '#fff',
    color: '#FF9800',
    padding: '12px 28px',
    borderRadius: 10,
    fontWeight: 600,
    fontSize: 16,
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    transition: 'background 0.2s',
  },
  donateIcon: {
    width: 20,
    height: 20,
    marginRight: 4,
    color: '#FF9800',
    flexShrink: 0,
  },
};

export default TambolaTicketGenerator; 